import { ExpandingTooltip } from "./ExpandingTooltip";
export {
  DlfPanel as TTDlfPanel,
  DlfPanelDropdown as TTDlfPanelDropdown,
  FieldStatusLabel as TTFieldStatusLabel,
  DlfMatcherTypeDropdown as TTDlfMatcherTypeDropdown,
  EditButton as TTEditButton,
  GenerateReuse as TTGenerateReuse,
  ClearButton as TTClearButton,
  GenerateReuseAll as TTGenerateReuseAll,
  AllowEmpty as TTAllowEmpty,
  ReusableButton as TTReusableButton,
  ConfidenceThreshold as TTConfidenceThreshold,
  ConfidenceLabel as TTConfidenceLabel,
  KeyValueMatcherKeyLabel as TTKeyValueMatcherKeyLabel,
  KeyValueMatcherValueLabel as TTKeyValueMatcherValueLabel,
  KeyValueMatcherValueSuggestions as TTKeyValueMatcherValueSuggestions,
};

// Item 6
const DlfPanel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Configure the rules to extract data from Document Level Fields (DLFs)
        and, optionally, transform and/or validate the extracted data.
      </p>
    );
  return (
    <>
      <p>
        In the <strong>Document Level Fields</strong> panel, you can configure
        the rules used to extract Document Level Fields (DLFs), also known as
        Header and Footer fields, when a document has been classified using this
        template.
      </p>
      <p>
        You can also configure template-specific validation and transformation
        rules for each field. Each DLF in a template will inherit any validation
        and transformation rules that have been configured for this Document
        Type in Batch Class Settings. Only configure new extraction or
        transformation rules that are specific to this template. Once
        configured, the template-specific rules will override any rules set at
        the Document Type level.
      </p>
    </>
  );
};

// Item 39A
const DlfPanelDropdown: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Select to switch between configuring data extraction, validation, or
        transformation for Document Level Fields at the template level.
      </p>
    );
  return (
    <>
      <p>
        Extraction is selected by default. Configure data extraction rules using
        this interface. Configure template-specific validation rules by
        selecting Validation or template-specific transformation rules by
        selecting Transformation.
      </p>
      <p>
        Each DLF in a template will inherit any validation and transformation
        rules that have been configured for this Document Type in Batch Class
        Settings. Only configure new extraction or transformation rules that are
        specific to this template. Once configured the template-specific rules
        will override any rules set for that field at the Document Type level.
      </p>
    </>
  );
};

// Item 7
const FieldStatusLabel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        One or multiple labels are displayed to indicate the status of the
        field.
      </p>
    );
  return (
    <>
      <p>The following labels can be displayed:</p>
      <p>
        <strong>Match</strong> - The configured extraction rule has fired and
        extracted data for this field.
      </p>
      <p>
        <strong>Blank</strong> - No extraction rule has been configured for this
        field.
      </p>
      <p>
        <strong>Error</strong> - The configured extraction rule has failed to
        extract any data.
      </p>
      <p>
        <strong>Empty</strong> - The configured extraction rule has failed to
        extract any data. However, the Allow Empty flag was set to On.
      </p>
      <p>
        <strong>Invalid</strong> - The extracted data doesn’t match the
        validation rules for the field.
      </p>
      <p>
        <strong>Reuse</strong> - The data was extracted via reuse.
      </p>
      <p>
        <strong>Transformed</strong> - A transformation has been applied to the
        extracted data. The transformed data will be displayed.
      </p>
      <p>
        <strong>Hardcode</strong> - The field has been configured as a hardcoded
        value. The hardcoded value will be displayed.
      </p>
    </>
  );
};

// Item 8
const DlfMatcherTypeDropdown: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Select the extraction rule type that you want to use for this
        field.{" "}
      </p>
    );
  return (
    <>
      <p>
        Select the extraction type you want to use for this DLF from the
        dropdown. The options are: Key Value Regex (default), Key Value Fuzzy,
        Regex and Hardcode.
      </p>
      <p>
        <strong>Key Value Regex</strong> extraction allows you to draw boxes
        around the Key and the Value on the document image. The system remembers
        the co-ordinates of each box, and their relative positions. When a
        document is classified using this template, Accelerator will use these
        co-ordinates to locate the Key and then extract the associated Value. By
        default, the Key will be the actual text inside the Key box (the system
        sets the regex as .+ which extracts all data within the Key box area),
        but you can modify this regex as required to make it more specific. The
        default is normally fine.
      </p>
      <p>
        <strong>Key Value Fuzzy</strong> extraction is similar to Key Value
        Regex extraction, but this option allows you to set a fuzzy % value for
        the Key. This is only used where the quality of the image is likely to
        be relatively poor, resulting in inconsistent OCR for the Key Value.
        With a fuzzy setting, the OCR doesn’t have to be an exact match with the
        Key Value that has been configured. For example, if the Key Value has
        been set to look for the string “Invoice Number”, but the extracted OCR
        is consistently extracting values that are not exact matches because of
        image quality issues (e.g., Inv0ice Number or Invoice Numbes) these
        values will never be picked up with a standard Key Value Regex. However,
        when a Key Value Fuzzy rule is configured, with a fuzzy percentage of
        less than 100% set, these Key Values are likely to be correctly
        extracted. The fuzzy % value can be set at any value from 50% to 100%.
        The lower the fuzzy % the more variability from the Key string will be
        permitted.
      </p>
      <p>
        <strong>Regex</strong> extraction allows you to enter any valid regular
        expression (regex) to extract the data when a Key Value Regex is
        inappropriate (e.g., the Key and Value fields are not located in a
        consistent relative position to one another.
      </p>
      <p>
        <strong>Hardcode</strong> extraction allows you to enter a value for the
        DLF that Accelerator will always enter for this field, for this
        template. This is useful when dealing with relatively poor-quality
        documents when the field may be extracted incorrectly due to OCR issues,
        but the value would rarely change (e.g., a supplier’s Company Number).
        It can also be used to hardcode values that are not located on the
        document and cannot be derived from lookups (e.g., Vendor ID).
      </p>
    </>
  );
};

// Item 10
const EditButton: ExpandingTooltip = () => (
  <p>
    Click to open/close the editing area for this field to configure or edit the
    rule. Clicking the coloured box to the left has the same function.
  </p>
);

// Item 11
const GenerateReuse: ExpandingTooltip = (short) => {
  if (short) return <p>Click to generate reuse options for this field.</p>;
  return (
    <>
      <p>
        For any Unknown or General Classification templates, the system will
        intelligently identify extraction rules from other templates as options
        for the current field. Only rules that extract valid data will be
        considered. An algorithm will rank these rules and the highest ranked
        rule will be used to extract the data when the document is processed in
        Transact.
      </p>
      <p>
        If the system is not already displaying reuse options for a field, this
        button will be labelled Generate Reuse. Click the button to generate the
        ranked reuse options. The highest ranked rule will be presented first.
        Click through the options until you find a suitable rule to use for the
        field.
      </p>
      <p>
        You will be able to see if a reuse option is available for this field by
        reviewing the document image. If the data that you want to extract has a
        box around it, in the same colour as the field you want to extract, then
        one of the reuse options will extract that data. Click through until you
        get to that reuse option.
      </p>
    </>
  );
};

// Item 12
const ClearButton: ExpandingTooltip = () => {
  return (
    <>
      <p>Click to delete all extraction rules for this DLF. </p>
    </>
  );
};

// Item 24
const GenerateReuseAll: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>Click to generate reuse options for all Document Level Fields. </p>
    );
  return (
    <>
      <p>
        For any Unknown or General Classification templates, the system will
        intelligently identify extraction rules from other templates as options
        for each Document Level Field. Only rules that extract valid data will
        be considered. An algorithm will rank these rules and the highest ranked
        rule will be used to extract the data when the document is processed in
        Transact.
      </p>
      <p>
        If the system is not already displaying reuse options, click this button
        to generate the ranked reuse options for all DLFs. The highest ranked
        rule will be presented first for each DLF that has reuse available.
        Click through the options until you find a suitable rule to use for a
        field.
      </p>
      <p>
        You will be able to see if a reuse option is available for each field by
        reviewing the document image. If the data that you want to extract has a
        box around it, in the same colour as a field you want to extract, then
        one of the reuse options will extract that data for the field.
      </p>
    </>
  );
};

// Item 40
const AllowEmpty: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>Toggle button to allow or disallow the field value to be empty.</p>
    );
  return (
    <>
      <p>
        When the <strong>Allow Empty</strong> button is toggled to On (a tick is
        displayed in the button) the extraction rules for the field will run
        but, if no data is extracted, the field will not stop in Transact for
        manual review. This setting is used when the data is sometimes present
        on the document but sometimes is missing, and you don’t want a document
        with missing data to always stop in Transact for manual review. Toggle
        the button to Off (a cross is displayed in the button) if you want the
        Operator to be forced to review the field in Transact if the extraction
        rule doesn’t fire.
      </p>
    </>
  );
};

// Item 41
const ReusableButton: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Toggle button to allow or disallow the extraction rule for this field to
        be made available for reuse.
      </p>
    );
  return (
    <>
      <p>
        When the <strong>Reusable</strong> button is toggled to On (a tick is
        displayed in the button) the extraction rule for the field will be
        included as a reuse option for this field on documents that are
        classified as the same document type but are not classified by a
        Specific template. Toggle the button to Off (a cross is displayed in the
        button) if you do not want the extraction rule to be available for
        reuse. Select the Off setting if you have used a very broad rule (such
        as .+) to avoid invalid data being extracted by reuse.
      </p>
    </>
  );
};

// Item 42
const ConfidenceThreshold: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>Set the confidence threshold for this Key Value extraction rule. </p>
    );
  return (
    <>
      <p>
        The default confidence threshold for each Document Level Field is set in
        Accelerator’s Batch Class Settings. If you want to change the threshold
        for this field, on this template, modify it here by clicking in the box
        and changing the percentage. This is rarely needed. You would use this
        capability if the Key could fire in multiple locations on a document and
        the Value is not in the same relative position. The higher the
        confidence percentage, the less movement it allows on the document.
      </p>
    </>
  );
};

// Item 43
const ConfidenceLabel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Accelerator’s confidence level for the Key Value extraction for the
        selected document.{" "}
      </p>
    );
  return (
    <>
      <p>
        This is Accelerator’s confidence level for the Key Value rule that has
        fired to extract the data for this field. If the Confidence is lower
        than the Confidence Threshold set for the field, the document will stop
        in Transact Validation for the field to be manually reviewed and the
        field will flag as Error in Accelerator.
      </p>
    </>
  );
};

// Item 44
const KeyValueMatcherKeyLabel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Use the tan coloured selection box that is displayed on the document
        image to select the Key for this Key Value extraction rule.
      </p>
    );
  return (
    <>
      <p>
        When selecting the Key on the image, select text that is consistently in
        the same relative position to the Value that you want to extract. The
        Key doesn’t have to be the label for that Value, just data that will
        appear on every sample in the same relative position.
      </p>
    </>
  );
};

// Item 45
const KeyValueMatcherValueLabel: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>
        Use the selection box on the document image that has the same colour as
        the DLF to select the Value for this Key Value extraction rule.
      </p>
    );
  return (
    <>
      <p>
        When selecting the Value on the image, select text that is consistently
        in the same relative position to the Key that you have used.
      </p>
    </>
  );
};

// Item 46
const KeyValueMatcherValueSuggestions: ExpandingTooltip = (short) => {
  if (short)
    return (
      <p>Click to display optional regexes that are valid for this field.</p>
    );
  return (
    <>
      <p>
        The regex options are populated from the validation rules configured in
        Template Management.
      </p>
    </>
  );
};
